import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { Helmet } from 'react-helmet';
import blogs from '../../data/blogs';
import BlogGraphic from './BlogGraphics';
import './BlogPost.css';

const components = {
  img: ({ src, alt }) => (
    <img
      src={src}
      alt={alt}
      className="blogpost-image"
    />
  ),
  a: ({ href, children }) => {
    const external = href && /^https?:\/\//i.test(href);
    return (
      <a href={href} {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
        {children}
      </a>
    );
  },
};

// ── Block renderer: blogs can be authored as a `blocks` array ──
function Block({ block }) {
  switch (block.type) {
    case 'md':
      return (
        <div className="blogpost-content">
          <ReactMarkdown components={components}>{block.value}</ReactMarkdown>
        </div>
      );
    case 'graphic':
      return <BlogGraphic name={block.name} />;
    case 'keypoints':
      return (
        <ul className="bp-keypoints">
          {block.items.map((it, i) => (
            <li key={i} className="bp-keypoint">
              <span className="bp-keypoint-mark">✓</span>
              <span>{it}</span>
            </li>
          ))}
        </ul>
      );
    case 'steps':
      return (
        <ol className="bp-steps">
          {block.items.map((s, i) => (
            <li key={i} className="bp-step">
              <span className="bp-step-n">{i + 1}</span>
              <div className="bp-step-body">
                <strong className="bp-step-title">{s.title}</strong>
                {s.text && <p className="bp-step-text">{s.text}</p>}
              </div>
            </li>
          ))}
        </ol>
      );
    case 'callout':
      return (
        <div className={`bp-callout bp-callout--${block.variant || 'note'}`}>
          {block.title && <p className="bp-callout-title">{block.title}</p>}
          <p className="bp-callout-text">{block.text}</p>
        </div>
      );
    case 'compare':
      return (
        <div className="bp-compare">
          {[block.left, block.right].map((col, ci) => (
            <div key={ci} className={`bp-compare-col bp-compare-col--${ci === 0 ? 'bad' : 'good'}`}>
              <p className="bp-compare-head">{col.title}</p>
              <ul className="bp-compare-list">
                {col.items.map((it, i) => <li key={i}>{it}</li>)}
              </ul>
            </div>
          ))}
        </div>
      );
    default:
      return null;
  }
}

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = blogs.find((b) => b.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return (
      <div className="blogpost-notfound">
        <p>Post not found.</p>
        <button onClick={() => navigate('/blog')}>← Back to blog</button>
      </div>
    );
  }

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.excerpt || "",
    "datePublished": post.isoDate || post.date,
    "dateModified": post.isoDate || post.date,
    "author": {
      "@type": "Organization",
      "name": "Dmyor",
      "url": "https://www.dmyor.com"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Dmyor",
      "logo": {
        "@type": "ImageObject",
        "url": "https://dmyor.com/images/og-image.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://www.dmyor.com/blog/${post.slug}`
    },
    "image": post.coverImage || "https://dmyor.com/images/og-image.png",
    "keywords": (post.keywords || []).join(", "),
    "url": `https://www.dmyor.com/blog/${post.slug}`
  };

  return (
    <div className="blogpost-page">
      <Helmet>
        <title>{`${post.title} — dmyor`}</title>
        <meta name="description" content={post.excerpt || post.description} />
        <link rel="canonical" href={`https://www.dmyor.com/blog/${post.slug}`} />
        <meta property="og:title" content={`${post.title} — dmyor`} />
        <meta property="og:description" content={post.excerpt || post.description} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://www.dmyor.com/blog/${post.slug}`} />
        <meta property="og:image" content={post.coverImage || "https://dmyor.com/images/og-image.png"} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${post.title} — dmyor`} />
        <meta name="twitter:description" content={post.excerpt || post.description} />
        <meta name="twitter:image" content={post.coverImage || "https://dmyor.com/images/og-image.png"} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="blogpost-container">
        <button className="blogpost-back" onClick={() => navigate('/blog')}>
          ← Back to blog
        </button>

        <div className="blogpost-header">
          <span className="blogpost-cluster-tag">{post.cluster}</span>
          <h1 className="blogpost-title">{post.title}</h1>
          <div className="blogpost-meta">
            <span>{post.date}</span>
            <span>·</span>
            <span>{post.readTime}</span>
          </div>
        </div>

        <hr className="blogpost-divider" />

        {Array.isArray(post.blocks) ? (
          <div className="blogpost-blocks">
            {post.blocks.map((b, i) => <Block key={i} block={b} />)}
          </div>
        ) : (
          <div className="blogpost-content">
            <ReactMarkdown components={components}>{post.content}</ReactMarkdown>
          </div>
        )}

        <div className="blogpost-cta">
          <div className="blogpost-cta-text">
            <p>Start on dmyor</p>
            <p>Write the reality that doesn't exist yet.</p>
          </div>
          <button
            className="blogpost-cta-btn"
            onClick={() => navigate(post.ctaLink || '/about')}
          >
            {post.ctaText || 'Start building →'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BlogPost;
